/**
 * The Voice Lab.
 *
 * Three rules the microphone imposes on the design:
 *
 * 1. **Explain before asking.** The system prompt is fired by one button, and the paragraph
 *    above it says what will be recorded, where it is stored, and that it never uploads. An
 *    app that asks during a tour is an app that gets denied.
 * 2. **Never imply recording that is not happening.** The red indicator is bound to
 *    `recorder.isCapturing`, which only becomes true when audio frames actually arrive.
 * 3. **Monitoring is dangerous by default.** Feeding a live microphone into a phone speaker
 *    is how feedback starts, so monitoring stays off until the user confirms headphones in a
 *    dialog that names the risk — there is no API that can answer that question for them.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { chip, icon, neonButton, segmentedRail, toggle, transportButton, valueStepper } from '../components/controls';
import { levelMeter } from '../components/meters';
import { confirmDialog, emptyState, processingOverlay } from '../components/sheet';
import { miniWaveform } from '../components/waveform';
import {
  EFFECT_KINDS,
  clampSampleEdit,
  effectKindDisplayName,
  effectKindSymbolName,
  makeEffectNode,
  makeSampleEdit,
  makeTrack,
  newUUID,
  padIsEmpty,
  type EffectKind,
  type SampleEdit,
} from '../../model/types';
import {
  analysis,
  encodeWav,
  engine,
  importAudioBlob,
  currentPattern,
  dropRecoveredTake,
  mutate,
  offStepValue,
  pendingRecoveredTakes,
  recoveredTakeBlob,
  notify,
  project,
  pushBanner,
  recorder,
  renderEdit,
  reportFailure,
  revision,
  saveRenderedAsAsset,
  selectedPadID,
  settings,
  stopPadForEditing,
  updateSettings,
  type RecordedTake,
} from '../../app/state';
import { downloadFile, shareContent, canShareFiles } from '../../platform/share';
import type { RecoverableTake } from '../../storage/takes';
import { callout, formatDuration, presentMenu, requiresProject, screenBody, screenHeader, section } from '../../app/shell';

/**
 * The voice transformations, each expressed as a chain of the same effect kinds the rest of
 * the app uses — nothing here is a special case the editor cannot also reach.
 */
interface VoicePreset {
  id: string;
  name: string;
  summary: string;
  build: () => SampleEdit;
}

function chainOf(...spec: Array<[EffectKind, Record<string, number>?]>): SampleEdit {
  const edit = makeSampleEdit();
  for (const [kind, values] of spec) {
    const node = makeEffectNode(kind);
    if (values !== undefined) Object.assign(node.values, values);
    edit.chain.nodes.push(node);
  }
  return edit;
}

const VOICE_PRESETS: readonly VoicePreset[] = [
  { id: 'higher', name: 'Higher', summary: 'Up five semitones, formants untouched.', build: () => chainOf(['pitchShift', { semitones: 5 }]) },
  { id: 'lower', name: 'Lower', summary: 'Down five semitones.', build: () => chainOf(['pitchShift', { semitones: -5 }]) },
  { id: 'formant', name: 'Formant', summary: 'Moves the voice character without moving the note.', build: () => chainOf(['formantShift', { shift: 4 }]) },
  { id: 'monster', name: 'Monster', summary: 'Deep, wide and slightly broken.', build: () => chainOf(['pitchShift', { semitones: -9 }], ['formantShift', { shift: -5 }], ['distortion', { drive: 0.3, mix: 0.6 }]) },
  { id: 'giant', name: 'Giant', summary: 'Low and enormous, in a big room.', build: () => chainOf(['pitchShift', { semitones: -7 }], ['reverb', { size: 0.85, mix: 0.4 }]) },
  { id: 'chipmunk', name: 'Chipmunk', summary: 'Way up, formants along for the ride.', build: () => chainOf(['pitchShift', { semitones: 10 }], ['formantShift', { shift: 6 }]) },
  { id: 'robot', name: 'Robot', summary: 'Ring modulation locked to a fixed pitch.', build: () => chainOf(['ringMod', { frequency: 90, mix: 0.9 }], ['bitCrush', { bits: 7, mix: 0.5 }]) },
  { id: 'radio', name: 'Radio', summary: 'Narrow band and compressed, like a broadcast.', build: () => chainOf(['bandPass', { frequency: 1600, bandwidth: 1.6 }], ['compressor', { threshold: -24, ratio: 8 }]) },
  { id: 'telephone', name: 'Telephone', summary: 'Tight band, a little distortion.', build: () => chainOf(['bandPass', { frequency: 1200, bandwidth: 0.8 }], ['distortion', { drive: 0.2, mix: 0.5 }]) },
  { id: 'megaphone', name: 'Megaphone', summary: 'Loud, mid-forward, clipped.', build: () => chainOf(['bandPass', { frequency: 2200, bandwidth: 1.2 }], ['distortion', { drive: 0.55, mix: 0.9 }], ['limiter', {}]) },
  { id: 'whisper', name: 'Whisper', summary: 'Gated and airy.', build: () => chainOf(['noiseGate', { threshold: -34 }], ['highPass', { frequency: 900 }], ['reverb', { size: 0.3, mix: 0.25 }]) },
  { id: 'alien', name: 'Alien', summary: 'Ring modulated and flanged.', build: () => chainOf(['ringMod', { frequency: 420, mix: 0.7 }], ['flanger', { depth: 0.8 }]) },
  { id: 'echo', name: 'Echo chamber', summary: 'Long repeats in a big space.', build: () => chainOf(['delay', { time: 0.42, feedback: 0.6, mix: 0.45 }], ['reverb', { size: 0.9, mix: 0.4 }]) },
  { id: 'reversed', name: 'Reversed', summary: 'Plays backwards.', build: () => { const edit = makeSampleEdit(); edit.isReversed = true; return edit; } },
  { id: 'stutter', name: 'Stutter', summary: 'Chopped into repeating slices.', build: () => chainOf(['stutter', { division: 4, gateLength: 0.4 }]) },
  { id: 'glitch', name: 'Glitch', summary: 'Rearranged, reversed and pitched, the same every time.', build: () => chainOf(['glitch', { density: 0.5 }]) },
  { id: 'distorted', name: 'Distorted', summary: 'Driven hard and squashed.', build: () => chainOf(['distortion', { drive: 0.7 }], ['compressor', {}]) },
  { id: 'chorus', name: 'Chorus', summary: 'Two detuned copies around the original.', build: () => chainOf(['chorus', { voices: 3, mix: 0.5 }]) },
  { id: 'harmonised', name: 'Harmonised', summary: 'Adds a third and a fifth above.', build: () => chainOf(['harmoniser', { voice1: 4, voice2: 7, mix: 0.6 }]) },
  { id: 'doubled', name: 'Doubled', summary: 'A tight second take, slightly late and detuned.', build: () => chainOf(['chorus', { rate: 0.15, depth: 0.2, voices: 2, mix: 0.45 }]) },
  { id: 'autotune', name: 'Pitch correction', summary: 'Snaps the take to the nearest note in key.', build: () => chainOf(['autoTune', { strength: 0.9 }]) },
  { id: 'vocoder', name: 'Vocoder-ish', summary: 'Band-limited ring modulation with a fixed carrier. A real vocoder needs a second signal.', build: () => chainOf(['ringMod', { frequency: 160, mix: 1 }], ['bandPass', { frequency: 1400, bandwidth: 2.4 }], ['chorus', { mix: 0.4 }]) },
];

/**
 * Takes outlive the screen.
 *
 * They used to live in a signal inside `voiceLabScreen()`, so the router tearing the screen
 * down took every unsaved recording with it — and finding a pad for a take means going to the
 * Library, which is exactly that. They are held here instead, keyed by project so switching
 * documents never carries one person's audio into another's, and the user is told on the way
 * out if anything is still unsaved.
 */
let takeStoreProjectID: string | null = null;
const takeStore = signal<readonly RecordedTake[]>([]);
const savedTakeIDs = new Set<string>();

/** Spoken form of an elapsed time, in words, so it is never read out digit by digit. */
function spokenElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  const secondsPart = `${rest} second${rest === 1 ? '' : 's'}`;
  if (minutes === 0) return secondsPart;
  return `${minutes} minute${minutes === 1 ? '' : 's'} ${secondsPart}`;
}

export function voiceLabScreen(): HTMLElement {
  if (project.peek() === null) return requiresProject();

  // Opening an editor for a sound stops that sound, and here it is not a nicety: the pad this
  // screen records into was left looping out of the speaker the microphone is pointed at.
  const recordingInto = selectedPadID.peek();
  if (recordingInto !== null) stopPadForEditing(recordingInto);

  const openProject = project.peek();
  if (takeStoreProjectID !== (openProject?.id ?? null)) {
    takeStoreProjectID = openProject?.id ?? null;
    takeStore.value = [];
    savedTakeIDs.clear();
  }
  const takes = takeStore;
  const activeTakeID = signal<string | null>(takeStore.peek()[takeStore.peek().length - 1]?.id ?? null);
  const preset = signal<VoicePreset | null>(null);
  const customEdit = signal<SampleEdit>(makeSampleEdit());
  const busy = signal(false);

  const permission = computed(() => recorder.permission.value);
  const capturing = computed(() => recorder.isCapturing.value);
  const paused = computed(() => recorder.isPaused.value);
  /**
   * Counting in is part of the take, not part of the idle state.
   *
   * `isCapturing` is deliberately false until frames arrive, which is the truth about the
   * microphone — but the button, the indicator and the live region all used it as "is a take
   * running", so for the two-and-a-half seconds of a count-in the record button said
   * "Record", the timer was hidden and nothing was announced. `hasOpenTake` is the signal
   * those three actually wanted.
   */
  const countingIn = computed(() => recorder.countInBeatsRemaining.value);
  const openTake = computed(() => recorder.hasOpenTake.value);

  const meter = levelMeter({ orientation: 'horizontal', segments: 24, showsClip: true, title: 'Microphone input', class: 'input-meter' });
  const stopMeter = effect(() => {
    const level = recorder.inputLevel.value;
    meter.setLevel(level.rms, level.peak);
  });

  const activeTake = computed(() => takes.value.find((take) => take.id === activeTakeID.value) ?? takes.value[0] ?? null);

  // --- Recording ---------------------------------------------------------------------------

  /**
   * Set once the user has confirmed headphones for this visit, either through the toggle or
   * through the "Monitor by default" preference. Monitoring can only be *applied* once the
   * microphone is open, because that is when the monitor gain node exists — so the intent is
   * held here and spent in `beginRecording`.
   */
  let monitoringArmed = false;

  /**
   * Bars of count-in, decided here rather than two screens away.
   *
   * Seeded from the app setting and the project's metronome so the Voice Lab agrees with the
   * rest of the app on arrival, then owned by this screen for the visit.
   */
  const countInBars = signal(
    settings.peek().countInEnabled ? Math.max(1, project.peek()?.metronome.countInBars ?? 1) : 0,
  );

  async function beginRecording(): Promise<void> {
    const open = project.peek();
    if (open === null) return;
    try {
      await engine.unlock();
      await recorder.start({
        // The count-in decision belongs where the decision is made. It used to live only on
        // the Settings screen, with its length taken from the project's metronome, so a
        // performer standing at the microphone had to leave, change it, and come back.
        countInBeats: countInBars.peek() * open.timeSignatureBeats,
        tempo: open.tempo,
        beatsPerBar: open.timeSignatureBeats,
        metronomeVolume: open.metronome.volume,
      });
      if (monitoringArmed && !recorder.isMonitoring.peek()) {
        // The chosen voice, not the dry microphone: monitoring was a bare gain node, so the
        // preset a user picked was inaudible until after the take was finished.
        const take = activeTake.peek();
        recorder.setMonitorChain(take === null ? livePreviewEdit().chain : currentEdit(take).chain);
        recorder.setMonitoring(true, { headphonesConfirmed: true });
      }
    } catch (error) {
      reportFailure('Recording could not start', error);
    }
  }

  /** The headphone confirmation, shared by the toggle and the "Monitor by default" setting. */
  function confirmMonitoring(): void {
    confirmDialog({
      title: 'Wearing headphones?',
      message:
        'Monitoring sends the microphone straight to the output. Through the phone speaker that becomes a feedback howl within a second. Only turn this on with headphones in.',
      confirmTitle: 'Yes, headphones are in',
      cancelTitle: 'Not now',
      onConfirm: () => {
        monitoringArmed = true;
        const allowed = recorder.setMonitoring(true, { headphonesConfirmed: true });
        if (!allowed && !recorder.isCapturing.peek()) {
          notify('Monitoring is armed', 'It switches on as soon as you start a take.');
        }
      },
    }).present();
  }

  async function endRecording(): Promise<void> {
    const wasCountingIn = countingIn.peek() > 0;
    try {
      const take = await recorder.stop();
      if (take === null) {
        if (wasCountingIn) {
          notify('Count-in stopped', 'Nothing was recorded.');
          return;
        }
        // The recorder knows *why*, and it is frequently not the microphone: a container
        // this browser wrote and then refused to decode produced "No audio frames arrived
        // from the microphone" while the recorder's own diagnosis, one field away, said the
        // recording could not be decoded. Blaming the wrong component sends the user to
        // check a permission that was never the problem.
        const reason = recorder.notice.peek();
        if (reason !== null && reason !== '') pushBanner({ kind: 'warning', title: 'That take was not kept', message: reason, timeout: 9000 });
        else notify('Nothing captured', 'No audio frames arrived from the microphone.');
        return;
      }
      const numbered: RecordedTake = { ...take, name: `Take ${takes.peek().length + 1}` };
      takes.value = [...takes.peek(), numbered];
      activeTakeID.value = numbered.id;
      // "Recording stopped" said the microphone closed and nothing else, so the one thing the
      // user was waiting for — that a take now exists, and which one — had to be hunted for.
      recordingLive.textContent = `${numbered.name} kept, ${spokenElapsed(numbered.duration)}.`;
    } catch (error) {
      reportFailure('The take could not be finished', error);
    }
  }

  // --- Rendering -------------------------------------------------------------------------------

  /** The chain the current preset would apply, with no take to bound it. */
  function livePreviewEdit(): SampleEdit {
    return preset.peek()?.build() ?? structuredClone(customEdit.peek());
  }

  function currentEdit(take: RecordedTake): SampleEdit {
    const base = preset.peek()?.build() ?? structuredClone(customEdit.peek());
    base.startTime = take.trimStart;
    base.endTime = take.trimEnd;
    return base;
  }

  async function renderTake(take: RecordedTake): Promise<Blob> {
    const rendered = await renderEdit(take.buffer, currentEdit(take), {
      tempo: project.peek()?.tempo ?? 96,
      sourceName: take.name,
    });
    return encodeWav(rendered, 24);
  }

  async function previewTake(take: RecordedTake): Promise<void> {
    await engine.unlock();
    if (preset.peek() === null && customEdit.peek().chain.nodes.length === 0) {
      engine.previewBuffer(take.buffer, { offset: take.trimStart, duration: take.trimEnd - take.trimStart });
      return;
    }
    const overlay = processingOverlay({ title: 'Rendering the preview…' });
    overlay.present();
    try {
      const rendered = await renderEdit(take.buffer, currentEdit(take), { tempo: project.peek()?.tempo ?? 96, sourceName: take.name });
      const ctx = engine.ensureContext();
      const out = ctx.createBuffer(rendered.channels.length, rendered.channels[0]?.length ?? 1, rendered.sampleRate);
      for (let index = 0; index < rendered.channels.length; index++) {
        out.copyToChannel(new Float32Array(rendered.channels[index]!), index);
      }
      engine.previewBuffer(out);
    } catch (error) {
      reportFailure('Preview failed', error);
    } finally {
      overlay.destroy();
    }
  }

  async function saveTake(take: RecordedTake, assign: boolean): Promise<void> {
    if (busy.peek()) return;
    busy.value = true;
    // What used to be here — "The raw recording is kept as well." — was not true: `saveTake`
    // makes exactly one persistence call, and it is the rendered WAV. The unprocessed audio
    // is saved only if the user picks "Import the raw take" from the row's menu.
    const overlay = processingOverlay({
      title: 'Rendering your take…',
      message: 'The processed version is saved. “Import the raw take” keeps the unprocessed one too.',
    });
    overlay.present();
    try {
      const blob = await renderTake(take);
      const name = preset.peek() === null ? take.name : `${take.name} · ${preset.peek()!.name}`;
      const asset = await saveRenderedAsAsset(blob.type === 'audio/wav' ? await engine.decode(await blob.arrayBuffer(), name) : take.buffer, name, 'microphone', ['voice']);
      savedTakeIDs.add(take.id);
      if (assign) {
        const padID = selectedPadID.peek();
        const open = project.peek();
        const pad =
          open === null
            ? undefined
            : (padID === null ? undefined : open.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === padID)) ??
              open.banks.flatMap((bank) => bank.pads).find((entry) => padIsEmpty(entry));
        if (pad === undefined) {
          notify('Saved to your library', 'Every pad is full, so nothing was replaced.');
        } else {
          mutate('Assign Recording', (target) => {
            const live = target.banks.flatMap((bank) => bank.pads).find((entry) => entry.id === pad.id);
            if (live === undefined) return;
            live.assetID = asset.id;
            // The same two lines `assignAssetToPad` runs. Writing only `assetID` and `name`
            // left the previous sound's trim and effect chain in place, so a fresh take
            // landed on a pad and played through a Monster chain, cropped to a region of a
            // file it had never seen.
            live.edit = structuredClone(asset.defaultEdit);
            clampSampleEdit(live.edit, asset.duration);
            live.name = name;
          });
          // The pad has been used, so the pending selection is spent. Leaving it set sent the
          // next take to a pad the user chose in another session.
          if (selectedPadID.peek() === pad.id) selectedPadID.value = null;
          notify('On the pad', name);
        }
      } else {
        notify('Saved to your library', name);
      }
    } catch (error) {
      reportFailure('That take could not be saved', error);
    } finally {
      overlay.destroy();
      busy.value = false;
    }
  }

  /**
   * Saves the take, puts it on a pad, and writes it into the pattern that is open.
   *
   * The Finish section offered library, pad and export and stopped there — "add it to a beat
   * or song" meant saving, leaving, going to the Sequencer and building the track by hand.
   * This does the whole thing: the sound lands on a pad, the pattern gains a track for that
   * pad, and the take is placed on the downbeat, ready to move.
   */
  async function addTakeToBeat(take: RecordedTake): Promise<void> {
    if (busy.peek()) return;
    const open = project.peek();
    if (open === null) return;
    await saveTake(take, true);
    const pad = project
      .peek()
      ?.banks.flatMap((bank) => bank.pads)
      .find((entry) => entry.name === (preset.peek() === null ? take.name : `${take.name} · ${preset.peek()!.name}`));
    if (pad === undefined) return;
    const patternID = currentPattern.peek()?.id;
    if (patternID === undefined) {
      notify('Saved to a pad', 'There is no pattern open yet. Make one in the Sequencer and the pad is waiting.');
      return;
    }
    mutate('Add Take To Pattern', (target) => {
      const pattern = target.patterns.find((entry) => entry.id === patternID);
      if (pattern === undefined) return;
      const existing = pattern.tracks.find((entry) => entry.padID === pad.id);
      const track =
        existing ?? makeTrack({ padID: pad.id, name: pad.name, length: pattern.length, color: pad.color });
      if (existing === undefined) pattern.tracks.push(track);
      while (track.steps.length < track.length) track.steps.push(offStepValue());
      const first = track.steps[0];
      if (first !== undefined) track.steps[0] = { ...first, isActive: true, velocity: 0.9 };
    });
    notify('In the beat', `“${pad.name}” is on the downbeat of ${currentPattern.peek()?.name ?? 'the pattern'}.`);
  }

  async function exportTake(take: RecordedTake): Promise<void> {
    try {
      const blob = await renderTake(take);
      const filename = `${take.name}.wav`;
      const file = new File([blob], filename, { type: 'audio/wav' });
      if (canShareFiles([file])) {
        const result = await shareContent({ title: take.name, files: [file] });
        if (result.outcome === 'shared') return;
      }
      downloadFile(blob, filename);
      notify('Saved', filename);
    } catch (error) {
      reportFailure('Export failed', error);
    }
  }

  // --- Recovered takes -----------------------------------------------------------------------
  //
  // Every take now reaches disk as it is captured, so a session ended by a phone call, a tab
  // discard or a crash leaves its audio behind rather than taking it. This is where it comes
  // back. Rows that were interrupted say so; rows that were simply never saved say that.

  const recovered = signal<readonly RecoverableTake[]>([]);
  const recoveryHost = h('div');

  async function refreshRecovered(): Promise<void> {
    const pending = await pendingRecoveredTakes().catch(() => []);
    // Anything already handed back as a live take this session is not "recovered".
    const live = new Set(takes.peek().map((take) => take.recoveryID));
    recovered.value = pending.filter((entry) => !live.has(entry.id));
  }

  async function restoreTake(entry: RecoverableTake): Promise<void> {
    const blob = await recoveredTakeBlob(entry);
    if (blob === null) {
      await dropRecoveredTake(entry.id);
      void refreshRecovered();
      return;
    }
    try {
      const buffer = await engine.decode(await blob.arrayBuffer(), entry.name);
      const restoredTake: RecordedTake = {
        id: newUUID(),
        name: `${entry.name} (recovered)`,
        blob,
        buffer,
        duration: buffer.duration,
        createdAt: entry.startedAt,
        mimeType: entry.mimeType,
        trimStart: 0,
        trimEnd: buffer.duration,
        recoveryID: entry.id,
      };
      takes.value = [...takes.peek(), restoredTake];
      activeTakeID.value = restoredTake.id;
      void refreshRecovered();
      notify('Take recovered', `${restoredTake.name}, ${spokenElapsed(restoredTake.duration)}.`);
    } catch (error) {
      // Undecodable here means undecodable everywhere in this browser; exporting it is the
      // only thing left that can rescue the performance, so the row stays and says so.
      reportFailure(`“${entry.name}” could not be reopened`, error);
    }
  }

  const stopRecovery = effect(() => {
    const list = recovered.value;
    recoveryHost.replaceChildren();
    if (list.length === 0) return;
    recoveryHost.appendChild(
      h(
        'p',
        { class: 'muted' },
        'These recordings are on this device but were never saved. PadLab kept them when the session ended.',
      ),
    );
    for (const entry of list) {
      recoveryHost.appendChild(
        h(
          'div',
          { class: 'take-row' },
          h(
            'span',
            { class: 'take-titles' },
            h('span', { class: 'take-name' }, entry.name),
            h(
              'span',
              { class: 'take-meta' },
              `${entry.interrupted ? 'Interrupted' : 'Not saved'} · ${spokenElapsed(entry.duration)}`,
            ),
          ),
          neonButton({ title: 'Reopen', style: 'secondary', onPress: () => void restoreTake(entry) }).el,
          neonButton({
            title: 'Discard',
            style: 'ghost',
            onPress: () => {
              confirmDialog({
                title: `Discard “${entry.name}”?`,
                message: 'The audio is deleted from this device. This cannot be undone.',
                confirmTitle: 'Discard',
                destructive: true,
                onConfirm: () => {
                  void dropRecoveredTake(entry.id).then(() => refreshRecovered());
                },
              }).present();
            },
          }).el,
        ),
      );
    }
  });
  void refreshRecovered();

  // --- Views -------------------------------------------------------------------------------------

  const permissionHost = h('div');
  const stopPermission = effect(() => {
    permissionHost.replaceChildren();
    switch (permission.value) {
      case 'granted':
        return;
      case 'denied':
        permissionHost.appendChild(
          callout(
            'Microphone blocked',
            'Safari is denying access',
            'In Safari, tap “aA” in the address bar → Website Settings → Microphone → Allow, then reload. In an installed PadLab, open the iOS Settings app → PadLab → Microphone. You can keep working without it: import a sound instead.',
            'danger',
          ),
        );
        return;
      case 'unsupported':
        permissionHost.appendChild(
          callout(
            'No microphone API',
            'This browser cannot record',
            'PadLab found no getUserMedia support here. Everything else works: import an audio file from the Library and edit it as usual.',
            'warning',
          ),
        );
        return;
      default:
        permissionHost.appendChild(
          callout(
            'Before we ask',
            'PadLab needs the microphone to record you',
            'Recording happens only while you hold this screen and press record. Takes are written to this device, never uploaded, and the microphone is released the moment you leave. Your browser will show its own permission prompt when you tap below.',
            'accent',
            h(
              'div',
              { class: 'callout-actions' },
              neonButton({
                title: 'Enable microphone',
                style: 'primary',
                onPress: () => {
                  updateSettings({ micEducationSeen: true });
                  void recorder.requestAccess();
                },
              }).el,
            ),
          ),
        );
    }
  });
  onCleanup(permissionHost, stopPermission);

  /**
   * The elapsed readout is written thirty times a second — `formatDuration` shows hundredths
   * below ten seconds, so the text genuinely changes on every meter tick. As a `role="status"`
   * live region that queued one announcement per tick and talked continuously for the whole
   * take, so the user could neither hear what they were recording nor find Stop by ear.
   *
   * It is a `role="timer"` with `aria-live="off"` now: read on demand, never volunteered. The
   * name is re-written once a second in words. What *should* speak — starting, pausing,
   * resuming, stopping — is a separate region carrying nothing else.
   */
  const indicator = h(
    'div',
    { class: 'record-indicator', role: 'timer', 'aria-live': 'off', 'aria-label': 'Elapsed recording time' },
    h('span', { class: 'record-dot', 'aria-hidden': 'true' }),
    h('span', { class: 'record-label' }, 'Recording'),
    h('span', { class: 'numeric record-time', 'aria-hidden': 'true' }, '0:00'),
  );
  const recordingLive = h('span', { class: 'visually-hidden', role: 'status', 'aria-live': 'polite' });

  let spokenPhase: 'off' | 'counting' | 'recording' | 'paused' = 'off';
  let spokenSecond = -1;
  const stopIndicator = effect(() => {
    const live = capturing.value;
    const isPaused = paused.value;
    const beatsLeft = countingIn.value;
    const counting = beatsLeft > 0;
    // Rule 2 of this module: never imply recording that is not happening. A take is open
    // from the press, but between the press and the first frame sit the permission round
    // trip and the count-in — neither of which is capture, so neither wears the record red
    // or the word "Recording".
    const pending = !live && !isPaused;
    indicator.classList.toggle('record-indicator--live', live);
    indicator.classList.toggle('record-indicator--paused', isPaused);
    indicator.classList.toggle('record-indicator--counting', pending);
    indicator.hidden = !openTake.value;
    const label = indicator.querySelector('.record-label');
    if (label !== null) {
      label.textContent = counting ? 'Counting in' : pending ? 'Getting ready' : isPaused ? 'Paused' : 'Recording';
    }
    const elapsed = recorder.elapsed.value;
    const time = indicator.querySelector('.record-time');
    if (time !== null) time.textContent = counting ? `-${beatsLeft}` : pending ? '' : formatDuration(elapsed);

    const second = Math.floor(elapsed);
    if (pending) {
      spokenSecond = -1;
      indicator.setAttribute(
        'aria-label',
        counting ? `Counting in, ${beatsLeft} beat${beatsLeft === 1 ? '' : 's'} to go` : 'Getting ready to record',
      );
    } else if (second !== spokenSecond) {
      spokenSecond = second;
      indicator.setAttribute('aria-label', `Elapsed recording time, ${spokenElapsed(second)}`);
    }

    const phase = counting ? 'counting' : !live && !isPaused ? 'off' : isPaused ? 'paused' : 'recording';
    if (phase !== spokenPhase) {
      const previous = spokenPhase;
      spokenPhase = phase;
      recordingLive.textContent =
        phase === 'counting'
          ? `Counting in, ${beatsLeft} beat${beatsLeft === 1 ? '' : 's'}`
          : phase === 'paused'
            ? 'Recording paused'
            : phase === 'recording'
              ? previous === 'paused'
                ? 'Recording resumed'
                : 'Recording started'
              : 'Recording stopped';
    }
  });

  const takesHost = h('div', { class: 'take-list' });
  const stopTakes = effect(() => {
    const list = takes.value;
    const active = activeTake.value;
    takesHost.replaceChildren();
    if (list.length === 0) {
      takesHost.appendChild(
        emptyState({
          symbol: 'mouth.fill',
          title: 'No takes yet',
          message: 'Every recording is kept until you leave this screen, so you can retake as often as you like without losing the good one.',
        }),
      );
      return;
    }
    for (const take of list) {
      const isActive = active?.id === take.id;
      const wave = miniWaveform({ tint: 'var(--accent-record)', class: 'take-wave' });
      void analysis
        .peaks(take.buffer, 60)
        .then((peaks) => wave.setPeaks(peaks))
        .catch(() => undefined);
      const row = h(
        'div',
        { class: ['take-row', isActive ? 'take-row--active' : ''] },
        h(
          'button',
          {
            class: 'take-main',
            type: 'button',
            // The selected take was previously stated in colour alone. `aria-pressed` is the
            // same statement made to anything that cannot see the row.
            'aria-pressed': isActive ? 'true' : 'false',
            onclick: () => {
              activeTakeID.value = take.id;
              void previewTake(take);
            },
          },
          h(
            'span',
            { class: 'take-name' },
            // A checkmark, not a coloured edge: the selected take is the only row wearing one,
            // and a glyph survives greyscale and a screenshot at arm's length.
            h('span', { class: 'take-check', 'aria-hidden': 'true' }, icon('checkmark', { size: 13 })),
            take.name,
          ),
          h('span', { class: 'kicker' }, `${formatDuration(take.duration)} · ${take.mimeType.split(';')[0] ?? 'audio'}`),
          wave.el,
        ),
        valueStepper({
          label: 'Trim start',
          value: take.trimStart,
          min: 0,
          max: Math.max(0.01, take.duration),
          step: 0.05,
          format: (value) => `${value.toFixed(2)} s`,
          onChange: (value) => {
            takes.value = takes.peek().map((entry) => (entry.id === take.id ? { ...entry, trimStart: Math.min(value, entry.trimEnd - 0.05) } : entry));
          },
        }).el,
        valueStepper({
          label: 'Trim end',
          value: take.trimEnd,
          min: 0,
          max: Math.max(0.01, take.duration),
          step: 0.05,
          format: (value) => `${value.toFixed(2)} s`,
          onChange: (value) => {
            takes.value = takes.peek().map((entry) => (entry.id === take.id ? { ...entry, trimEnd: Math.max(value, entry.trimStart + 0.05) } : entry));
          },
        }).el,
        h(
          'button',
          {
            class: 'take-more',
            type: 'button',
            'aria-label': `Actions for ${take.name}`,
            onclick: () =>
              presentMenu(take.name, [
                { title: 'Preview', symbol: 'play.fill', onSelect: () => void previewTake(take) },
                { title: 'Save to library', symbol: 'folder.fill', onSelect: () => void saveTake(take, false) },
                { title: 'Save and put on a pad', symbol: 'square.grid.3x3.fill', onSelect: () => void saveTake(take, true) },
                { title: 'Export', symbol: 'square.and.arrow.up', onSelect: () => void exportTake(take) },
                {
                  title: 'Import the raw take',
                  symbol: 'waveform.path',
                  detail: 'Unprocessed, straight from the microphone',
                  onSelect: () => {
                    void importAudioBlob(take.blob, `${take.name} raw`, 'microphone', { tags: ['voice', 'raw'] })
                      .then((result) => notify('Imported', result.asset.name))
                      .catch((error: unknown) => reportFailure('Import failed', error));
                  },
                },
                {
                  title: 'Discard take',
                  symbol: 'trash.fill',
                  destructive: true,
                  onSelect: () => {
                    takes.value = takes.peek().filter((entry) => entry.id !== take.id);
                  },
                },
              ]),
          },
          icon('slider.horizontal.3', { size: 17 }),
        ),
      );
      onCleanup(row, () => wave.destroy());
      takesHost.appendChild(row);
    }
  });
  onCleanup(takesHost, stopTakes);

  const presetHost = h('div', { class: 'preset-grid' });
  for (const entry of VOICE_PRESETS) {
    const button = h(
      'button',
      {
        class: 'preset-card',
        type: 'button',
        onclick: () => {
          preset.value = preset.peek()?.id === entry.id ? null : entry;
          const take = activeTake.peek();
          if (take !== null) void previewTake(take);
        },
      },
      h(
        'span',
        { class: 'preset-name' },
        h('span', { class: 'preset-check', 'aria-hidden': 'true' }, icon('checkmark', { size: 13 })),
        entry.name,
      ),
      h('span', { class: 'preset-summary' }, entry.summary),
    );
    const stop = effect(() => {
      const on = preset.value?.id === entry.id;
      button.classList.toggle('preset-card--on', on);
      // The loaded preset is a toggle, so it says so programmatically as well as visually.
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    onCleanup(button, stop);
    presetHost.appendChild(button);
  }

  const screen = h(
    'div',
    { class: 'screen screen--voice' },
    screenHeader({
      kicker: 'Record and transform',
      title: 'Voice Lab',
      showsBack: false,
      actions: [indicator],
    }),
    screenBody(
      permissionHost,
      section(
        'Record',
        recordingLive,
        meter.el,
        h(
          'div',
          { class: 'record-controls' },
          transportButton({
            kind: 'record',
            // Active from the press, not from the first frame: this is what flips the spoken
            // name to "Stop recording" while the count-in runs, so the button never lies
            // about what pressing it will do.
            active: openTake,
            enabled: computed(() => permission.value !== 'unsupported'),
            onPress: () => {
              if (openTake.peek()) void endRecording();
              else void beginRecording();
            },
          }).el,
          neonButton({
            title: computed(() => (paused.value ? 'Resume' : 'Pause')),
            style: 'secondary',
            // Not `capturing`: pause sets that false, so gating on it disabled the button
            // that would have resumed and the button that would have discarded, leaving the
            // record button — which used to destroy the paused take — as the only live one.
            enabled: openTake,
            onPress: () => {
              if (recorder.isPaused.peek()) recorder.resume();
              else recorder.pause();
            },
          }).el,
          neonButton({
            title: 'Discard',
            style: 'ghost',
            enabled: openTake,
            onPress: () => recorder.cancel(),
          }).el,
        ),
        toggle({
          title: 'Monitor through headphones',
          subtitle: 'Hear yourself while recording. Confirm headphones first: monitoring into a speaker causes feedback.',
          on: computed(() => recorder.isMonitoring.value || (monitoringArmed && !recorder.isCapturing.value)),
          onChange: (on) => {
            if (!on) {
              monitoringArmed = false;
              recorder.setMonitoring(false);
              return;
            }
            confirmMonitoring();
          },
        }).el,
        h('p', { class: 'muted' }, computed(() => recorder.notice.value ?? '')),
        h(
          'p',
          { class: 'kicker' },
          computed(() => {
            const supported = recorder.supportedMimeTypes();
            return supported.length === 0 ? 'No recording format available' : `Recording as ${supported[0]}`;
          }),
        ),
      ),
      section(
        'Count-in',
        segmentedRail({
          items: [
            { id: '0', title: 'Off' },
            { id: '1', title: '1 bar' },
            { id: '2', title: '2 bars' },
          ],
          selection: computed(() => String(countInBars.value)),
          label: 'Count-in before recording',
          onSelect: (id) => {
            const bars = Number.parseInt(id, 10);
            countInBars.value = bars;
            // Kept in step with the app-wide preference, so the Settings screen and this rail
            // never disagree about whether a count-in happens.
            updateSettings({ countInEnabled: bars > 0 });
          },
        }).el,
        h('p', { class: 'muted' }, 'Clicks before capture starts, so you can come in on the beat. The recording light stays off until the count is over.'),
      ),
      section('Recovered', recoveryHost),
      section('Takes', takesHost),
      section(
        'Transform',
        h(
          'div',
          { class: 'chip-row scroll-x' },
          chip({
            title: 'None',
            // No transform is this rail's resting state. A filled acid "None" claims
            // something is armed while the take is going out exactly as it came in.
            resting: true,
            selected: computed(() => preset.value === null && customEdit.value.chain.nodes.length === 0),
            onPress: () => {
              preset.value = null;
              customEdit.value = makeSampleEdit();
            },
          }).el,
          chip({
            title: 'Custom chain',
            selected: computed(() => preset.value === null && customEdit.value.chain.nodes.length > 0),
            onPress: () => {
              preset.value = null;
              presentMenu(
                'Build your own chain',
                EFFECT_KINDS.map((kind) => ({
                  title: effectKindDisplayName(kind),
                  symbol: effectKindSymbolName(kind),
                  onSelect: () => {
                    const next = structuredClone(customEdit.peek());
                    next.chain.nodes.push(makeEffectNode(kind));
                    customEdit.value = next;
                    notify('Added to your chain', effectKindDisplayName(kind));
                  },
                })),
              );
            },
          }).el,
        ),
        presetHost,
      ),
      section(
        'Finish',
        h(
          'div',
          { class: 'export-actions' },
          neonButton({
            title: 'Save and put on a pad',
            style: 'primary',
            enabled: computed(() => activeTake.value !== null && !busy.value),
            onPress: () => {
              const take = activeTake.peek();
              if (take !== null) void saveTake(take, true);
            },
          }).el,
          neonButton({
            title: 'Save to library',
            style: 'secondary',
            enabled: computed(() => activeTake.value !== null && !busy.value),
            onPress: () => {
              const take = activeTake.peek();
              if (take !== null) void saveTake(take, false);
            },
          }).el,
          neonButton({
            title: 'Add to the beat',
            style: 'secondary',
            enabled: computed(() => activeTake.value !== null && !busy.value),
            onPress: () => {
              const take = activeTake.peek();
              if (take !== null) void addTakeToBeat(take);
            },
          }).el,
          neonButton({
            title: 'Export',
            style: 'ghost',
            enabled: computed(() => activeTake.value !== null),
            onPress: () => {
              const take = activeTake.peek();
              if (take !== null) void exportTake(take);
            },
          }).el,
        ),
      ),
    ),
  );

  const stopRevision = effect(() => {
    revision.value;
  });

  // "Monitor by default" was a preference nothing read. It cannot simply be applied — the
  // headphone confirmation is the whole safety mechanism, and the settings copy promises it —
  // so it pre-opens that confirmation once, on entry, before anything is recording.
  if (settings.peek().monitoringDefault && permission.peek() === 'granted' && !recorder.isMonitoring.peek()) {
    confirmMonitoring();
  }

  onCleanup(screen, () => {
    stopMeter();
    stopIndicator();
    stopRevision();
    stopRecovery();
    meter.destroy();
    // Leaving the screen releases the device. A live microphone track behind a screen the
    // user has walked away from is exactly the thing an indicator is supposed to prevent.
    recorder.releaseDevice();
    engine.stopPreview();

    // Takes are written to the blob store as they are captured, so leaving the screen — or
    // the tab being discarded — no longer costs one. They are still not *in the library*
    // until the user says so, and that is what this says now: it used to warn that they
    // would be lost, which is no longer true and would have taught people to distrust a
    // recovery path that works.
    const unsaved = takes.peek().filter((entry) => !savedTakeIDs.has(entry.id));
    if (unsaved.length > 0) {
      pushBanner({
        kind: 'info',
        title: `${unsaved.length} take${unsaved.length === 1 ? '' : 's'} not in your library yet`,
        message:
          'PadLab kept them on this device. Reopen the Voice Lab and they are under “Recovered”. Save one to the library to use it on a pad.',
        timeout: 8000,
      });
    }
  });
  return screen;
}
